/**
 * G5 setup: set a few shared-config values on a drafted `viko.project.yaml` text, keeping its
 * comments and layout (the reviewed file is what gets committed). Paths are dotted keys such as
 * `landing.mergeMode`; `landing.test` / `landing.build` put the command FIRST in their list
 * (deduplicated) rather than replacing the audit's findings. The result must still validate.
 */
import { isMap, isSeq, parseDocument } from 'yaml';
import { parseProjectConfig } from './parse.js';
const LIST_PATHS = new Set(['landing.test', 'landing.build']);
/** Only these shared settings may be set this way (mirrors `@viko/protocol` SETUP_CONFIG_KEYS). */
export const SETTABLE_CONFIG_PATHS = [
    'loopProfile',
    'autonomy.level',
    'landing.mergeMode',
    'landing.targetBranch',
    'landing.test',
    'landing.build',
    'routing.defaultAgent',
    'worktree.strategy',
];
export function setConfigValues(text, sets) {
    const doc = parseDocument(text);
    if (doc.errors.length)
        throw new Error('config is not valid YAML');
    if (!isMap(doc.contents))
        throw new Error('config is not a mapping');
    for (const s of sets) {
        if (!SETTABLE_CONFIG_PATHS.includes(s.path))
            throw new Error(`bad path ${s.path}`);
        const keys = s.path.split('.');
        if (LIST_PATHS.has(s.path)) {
            const cur = doc.getIn(keys);
            const items = isSeq(cur)
                ? cur.items.map((i) => String(i.value ?? i))
                : [];
            doc.setIn(keys, [s.value, ...items.filter((i) => i !== s.value)]);
        }
        else if (s.path === 'worktree.strategy') {
            // Switching strategy replaces the block (pool/single-lane carry their own fields).
            doc.setIn(['worktree'], { strategy: s.value });
        }
        else
            doc.setIn(keys, s.value);
    }
    const out = doc.toString({ lineWidth: 100 });
    const parsed = parseProjectConfig(out);
    if (!parsed.ok)
        throw new Error('config is not valid after the change');
    return out;
}
/** Apply patch ops, keeping comments and layout; the result must still validate. */
export function applyConfigPatch(text, ops) {
    let cur = text;
    for (const op of ops) {
        const doc = parseDocument(cur);
        if (doc.errors.length || !isMap(doc.contents))
            throw new Error('config is not a mapping');
        if (op.op === 'setQuickCheck') {
            doc.setIn(['execution', 'quickCheck'], op.commands);
        }
        else if (op.op === 'addRoutingHint') {
            // The schema's default hints apply only while `routing.hints` is absent: materialize them
            // first so adding one never drops the defaults.
            const parsed = parseProjectConfig(cur);
            if (!parsed.ok)
                throw new Error('config is not valid');
            const hints = parsed.config.routing.hints.map((h) => JSON.parse(JSON.stringify(h)));
            const labels = [...op.labels].sort().join('\u0000');
            const i = hints.findIndex((h) => [...h.labels].sort().join('\u0000') === labels);
            if (i >= 0)
                hints[i] = { ...hints[i], tier: op.tier };
            else
                hints.push({ labels: op.labels, tier: op.tier });
            doc.setIn(['routing', 'hints'], hints);
        }
        else {
            const cur2 = doc.getIn(['execution', 'models']);
            const items = isSeq(cur2) ? cur2.toJSON() : [];
            const i = items.findIndex((m) => m.agent === op.agent && m.tier === op.tier);
            if (i >= 0)
                items[i] = { ...items[i], note: op.note };
            else
                items.push({ agent: op.agent, tier: op.tier, note: op.note });
            doc.setIn(['execution', 'models'], items);
        }
        cur = doc.toString({ lineWidth: 100 });
    }
    const parsed = parseProjectConfig(cur);
    if (!parsed.ok)
        throw new Error('config is not valid after the change');
    return cur;
}
//# sourceMappingURL=patch.js.map